const GTM_SCRIPT_ID = 'uticket-gtm-script';
const GTM_SCRIPT_SRC_BASE = 'https://www.googletagmanager.com/gtm.js';
const DEFAULT_GTM_IDLE_TIMEOUT_MS = 2500;

let hasInitializedGtm = false;
let hasScheduledGtmInit = false;

type IdleDeadline = {
	didTimeout: boolean;
	timeRemaining: () => number;
};

type WindowWithTracking = Window & {
	dataLayer?: unknown[];
	gtag?: (...args: unknown[]) => void;
	requestIdleCallback?: (
		callback: (deadline: IdleDeadline) => void,
		options?: { timeout: number }
	) => number;
};

const isDevelopment = process.env.NODE_ENV === 'development';

const normalizeGtmId = (gtmId?: string | null) => {
	if (typeof gtmId !== 'string') return '';
	return gtmId.trim();
};

const getTrackingWindow = (): WindowWithTracking => window as WindowWithTracking;

const debugLog = (...values: unknown[]) => {
	if (!isDevelopment) return;
	console.info('[tracking]', ...values);
};

const warnLog = (...values: unknown[]) => {
	if (!isDevelopment) return;
	console.warn('[tracking]', ...values);
};

const hasGtmScriptInDom = (gtmId: string) => {
	if (typeof document === 'undefined') return false;
	if (document.getElementById(GTM_SCRIPT_ID)) return true;
	return Boolean(
		document.querySelector(
			`script[src*="${GTM_SCRIPT_SRC_BASE}?id=${encodeURIComponent(gtmId)}"]`
		)
	);
};

export const ensureDataLayer = (): unknown[] => {
	if (typeof window === 'undefined') return [];

	const trackingWindow = getTrackingWindow();
	trackingWindow.dataLayer = trackingWindow.dataLayer || [];
	return trackingWindow.dataLayer;
};

export const ensureGtag = (): ((...args: unknown[]) => void) | undefined => {
	if (typeof window === 'undefined') return undefined;

	const trackingWindow = getTrackingWindow();
	ensureDataLayer();

	if (typeof trackingWindow.gtag !== 'function') {
		trackingWindow.gtag = (...args: unknown[]) => {
			ensureDataLayer().push(args);
		};
	}

	return trackingWindow.gtag;
};

export const pushDataLayer = (payload: unknown): void => {
	ensureDataLayer().push(payload);
};

export function pushDataLayerArgs(...args: unknown[]): void {
	void args;
	// eslint-disable-next-line prefer-rest-params
	ensureDataLayer().push(arguments);
}

export const initGtmOnce = (gtmId?: string | null): boolean => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return false;
	}

	const normalizedGtmId = normalizeGtmId(gtmId);
	if (!normalizedGtmId) {
		warnLog('Skipping GTM init because GTM ID is empty.');
		return false;
	}

	if (hasInitializedGtm || hasGtmScriptInDom(normalizedGtmId)) {
		hasInitializedGtm = true;
		debugLog('Skipping GTM init because it is already initialized.');
		return false;
	}

	ensureDataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });

	const script = document.createElement('script');
	script.id = GTM_SCRIPT_ID;
	script.async = true;
	script.src = `${GTM_SCRIPT_SRC_BASE}?id=${encodeURIComponent(normalizedGtmId)}`;

	const firstScriptTag = document.getElementsByTagName('script')[0];
	if (firstScriptTag?.parentNode) {
		firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
	} else {
		document.head.appendChild(script);
	}

	hasInitializedGtm = true;
	debugLog(`Initialized GTM with ID "${normalizedGtmId}".`);
	return true;
};

export const scheduleIdleTask = (
	callback: () => void,
	timeoutMs: number = DEFAULT_GTM_IDLE_TIMEOUT_MS
): void => {
	if (typeof window === 'undefined') return;

	const trackingWindow = getTrackingWindow();
	if (typeof trackingWindow.requestIdleCallback === 'function') {
		trackingWindow.requestIdleCallback(() => callback(), {
			timeout: timeoutMs,
		});
		return;
	}

	window.setTimeout(callback, timeoutMs);
};

export const scheduleAfterInteractionOrIdle = (
	callback: () => void,
	timeoutMs: number = 8000
): void => {
	if (typeof window === 'undefined') return;

	let hasExecuted = false;
	const listenerOptions: AddEventListenerOptions = {
		passive: true,
		once: true,
	};
	const events: Array<keyof WindowEventMap> = [
		'pointerdown',
		'touchstart',
		'keydown',
		'scroll',
	];

	const execute = () => {
		if (hasExecuted) return;
		hasExecuted = true;
		events.forEach(eventName => {
			window.removeEventListener(eventName, execute, listenerOptions);
		});
		scheduleIdleTask(callback, 0);
	};

	events.forEach(eventName => {
		window.addEventListener(eventName, execute, listenerOptions);
	});

	window.setTimeout(execute, timeoutMs);
};

export const scheduleGtmInit = (
	gtmId?: string | null,
	timeoutMs: number = DEFAULT_GTM_IDLE_TIMEOUT_MS
): void => {
	if (typeof window === 'undefined') return;

	const normalizedGtmId = normalizeGtmId(gtmId);
	if (!normalizedGtmId) {
		warnLog('Skipping GTM schedule because GTM ID is empty.');
		return;
	}

	if (hasInitializedGtm || hasScheduledGtmInit) {
		return;
	}

	hasScheduledGtmInit = true;

	const executeInit = () => {
		initGtmOnce(normalizedGtmId);
	};

	scheduleIdleTask(executeInit, timeoutMs);
};
